// A picture's Parameters (#241): live's Transform, Flip, Opacity, Stroke, Shadow, Radius and
// Chroma Key groups for a video or a still, in live's order.
import { Button } from "@mantine/core";
import { FlipHorizontal2, FlipVertical2, RotateCw } from "lucide-react";
import type { ReactNode } from "react";

import { AnimationsSection } from "./AnimationsSection";
import type { Size } from "./engine/project";
import classes from "./editor.module.css";
import {
  ColorField,
  ExtraSection,
  LevelSlider,
  LookNumber,
  LookRow,
  ParamSection,
  ShadowFields,
  StrokeFields,
} from "./params";
import { fittedBox } from "./size";
import {
  ADDED_CHROMA_KEY,
  ADDED_SHADOW,
  ADDED_STROKE,
  MAX_RADIUS,
  type ChromaKey,
} from "./look";
import type { ClipPatch, ImageClip, TimelineEdit, VideoClip } from "./timeline";

/**
 * Live's groups for a picture (#241), in live's order: Transform (Position X and Y, Size W and H,
 * Rotation), Flip (X, Y), Opacity (0 to 100 %), Stroke and Shadow (opened by "+"), Radius (0 to
 * 500 px), Chroma Key (opened by "+") and Animations (#266, `AnimationsSection`). Flip, Opacity and
 * Radius are always open (the lead's decision: live's "+" on them does nothing). Until the
 * Transform is first set, its numbers are those of the picture fitted in the frame, as drawn.
 */
export function LookSections(props: {
  clip: VideoClip | ImageClip;
  frame: Size;
  picture: Size | null;
  /** A video's Remove background, just above Chroma Key (spec remove-background.md). */
  background?: ReactNode;
  onEdit: (edit: TimelineEdit) => void;
}) {
  const { clip, frame, picture, onEdit } = props;
  const set = (patch: ClipPatch) => onEdit({ type: "setClip", id: clip.id, patch });
  const box =
    clip.box ??
    (picture === null ? { x: 0, y: 0, width: frame.width, height: frame.height } : fittedBox(frame, picture));
  const setBox = (change: Partial<typeof box>) => set({ box: { ...box, ...change } });
  const flip = (axis: "X" | "Y") => {
    const on = axis === "X" ? clip.flipX : clip.flipY;
    return (
      <Button
        variant="default"
        size="xs"
        className={classes.flipButton}
        aria-pressed={on}
        data-on={on}
        leftSection={axis === "X" ? <FlipHorizontal2 size={14} /> : <FlipVertical2 size={14} />}
        onClick={() => set(axis === "X" ? { flipX: !on } : { flipY: !on })}
      >
        {axis}
      </Button>
    );
  };
  return (
    <>
      <ParamSection title="Transform">
        <LookRow label="Position">
          <LookNumber label="Position X" tag="X" value={box.x} onCommit={(x) => setBox({ x })} />
          <LookNumber label="Position Y" tag="Y" value={box.y} onCommit={(y) => setBox({ y })} />
        </LookRow>
        <LookRow label="Size">
          <LookNumber label="Size W" tag="W" value={box.width} min={1} onCommit={(width) => setBox({ width })} />
          <LookNumber label="Size H" tag="H" value={box.height} min={1} onCommit={(height) => setBox({ height })} />
        </LookRow>
        <LookRow label="Rotation">
          <LookNumber
            label="Rotation"
            tag={<RotateCw size={12} aria-hidden />}
            value={clip.rotation}
            onCommit={(rotation) => set({ rotation })}
          />
        </LookRow>
      </ParamSection>
      <ParamSection title="Flip">
        <LookRow label="Flip">
          {flip("X")}
          {flip("Y")}
        </LookRow>
      </ParamSection>
      <ParamSection title="Opacity">
        <LevelSlider
          label="Opacity"
          bare
          value={Math.round(clip.opacity * 100)}
          max={100}
          unit="%"
          onChange={(percent) => set({ opacity: percent / 100 })}
        />
      </ParamSection>
      <ExtraSection
        title="Stroke"
        open={clip.stroke !== null}
        onAdd={() => set({ stroke: ADDED_STROKE })}
        onRemove={() => set({ stroke: null })}
      >
        {clip.stroke === null ? null : (
          <StrokeFields stroke={clip.stroke} onChange={(stroke) => set({ stroke })} />
        )}
      </ExtraSection>
      <ExtraSection
        title="Shadow"
        open={clip.shadow !== null}
        onAdd={() => set({ shadow: ADDED_SHADOW })}
        onRemove={() => set({ shadow: null })}
      >
        {clip.shadow === null ? null : (
          <ShadowFields shadow={clip.shadow} onChange={(shadow) => set({ shadow })} />
        )}
      </ExtraSection>
      <ParamSection title="Radius">
        <LevelSlider
          label="Radius"
          bare
          value={clip.radius}
          max={MAX_RADIUS}
          unit="px"
          onChange={(radius) => set({ radius })}
        />
      </ParamSection>
      {props.background}
      <ExtraSection
        title="Chroma Key"
        open={clip.chromaKey !== null}
        onAdd={() => set({ chromaKey: ADDED_CHROMA_KEY })}
        onRemove={() => set({ chromaKey: null })}
      >
        {clip.chromaKey === null ? null : (
          <KeyFields chromaKey={clip.chromaKey} onChange={(chromaKey) => set({ chromaKey })} />
        )}
      </ExtraSection>
      <AnimationsSection clip={clip} onEdit={onEdit} />
    </>
  );
}

/** Live's Chroma Key: its Key Color, Similarity and Spill (0 to 100 %). */
function KeyFields(props: { chromaKey: ChromaKey; onChange: (chromaKey: ChromaKey) => void }) {
  const { chromaKey, onChange } = props;
  return (
    <>
      <LookRow label="Key Color">
        <ColorField label="Key color" value={chromaKey.color} onCommit={(color) => onChange({ ...chromaKey, color })} />
      </LookRow>
      <LevelSlider
        label="Similarity"
        value={Math.round(chromaKey.similarity * 100)}
        max={100}
        unit="%"
        onChange={(percent) => onChange({ ...chromaKey, similarity: percent / 100 })}
      />
      <LevelSlider
        label="Spill"
        value={Math.round(chromaKey.spill * 100)}
        max={100}
        unit="%"
        onChange={(percent) => onChange({ ...chromaKey, spill: percent / 100 })}
      />
    </>
  );
}
